/* MS-ENTRY-003 — public cross-tenant shops directory (platform /shops).
 * Tenant-scoped variant: `/:tenantCode/shops` passes filterTenantCode.
 */

import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  resolveLocalizedName,
  toSameOriginCatalogImageUrl,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
} from 'pi-kiosk-shared';
import { useTheme } from '../../app/ThemeProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  readDirectoryShopsPersist,
  resolveDirectoryShopsPersistTenant,
  writeDirectoryShopsPersist,
} from '../../shared/session/searchPersistStorage.js';
import { isAccountAccessUsable } from '../account/accountApi.js';
import {
  DIRECTORY_UI_PREFS_HYDRATED_EVENT,
  putDirectoryShopsSortRemote,
} from '../account/useDirectoryUiPreferenceSync.js';
import { invalidatePublicCustomerSalesPointsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import {
  fetchPublicCustomerSalesPoints,
  type PublicCustomerSalesPointDTO,
} from './publicSalesPointsApi.js';
import { buildShopDirectoryDistanceMeta } from './shopDirectoryDistanceMeta.js';
import { resolveShopLocationLabelWithT } from './resolveShopLocationLabel.js';
import { ShopDirectoryRowView } from './ShopDirectoryRowView.js';
import {
  setSalesPointFavorite,
  useSalesPointContactsByToken,
} from '../account/salesPointContactsApi.js';
import {
  isPendingFavoriteSalesPointId,
  removePendingFavoriteSpContact,
  stagePendingFavoriteSpContact,
} from '../account/pendingFavoriteSpContacts.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import { resolveSalesPointDescription } from './resolveSalesPointDescription.js';
import { CUSTOMER_CONTENT_FULL_CLASS, CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS } from '../../app/customerTopBarStyles.js';
import {
  ShopsDirectoryMap,
  type ShopsDirectoryMapRequestMyLocationOptions,
} from './ShopsDirectoryMap.js';
import { useShopsDirectoryMapViews } from './useShopsDirectoryMapViews.js';
import { useDirectoryViewModeSearch } from './useDirectoryViewModeSearch.js';
import { DirectoryFindToolbar } from '../../shared/ui/DirectoryFindToolbar.js';
import { DirectoryListMapViewSwitch } from '../../shared/ui/DirectoryListMapViewSwitch.js';
import { DirectoryPageHeader } from '../../shared/ui/DirectoryPageHeader.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import {
  buildOrgFacets,
  filterDirectoryShops,
  formatDistanceMeters,
  groupDirectoryShopsByDistance,
  mapDirectorySortToApiSort,
  type DirectoryDistanceBucketId,
  type DirectorySortId,
} from '../../shared/ui/directoryListFilter.js';
import {
  readDirectoryGeoAllowedFlag,
  writeDirectoryGeoAllowedFlag,
} from '../../shared/ui/directoryGeoAllowed.js';
import {
  decideDirectoryGeoWarm,
  type DirectoryGeoPermissionProbe,
} from '../../shared/ui/directoryGeoWarmDecision.js';
import {
  DIRECTORY_DENSITY_STORAGE_KEYS,
  useDirectoryDensity,
  type DirectoryDensityMode,
} from '../../shared/ui/directoryDensity.js';
import {
  DIRECTORY_CARD_ITEM_CLASS,
  DIRECTORY_CARD_PAD_CLASS,
  DIRECTORY_EMPTY_PANEL_MAX_CLASS,
  DIRECTORY_PAGE_STACK_CLASS,
  DIRECTORY_FAVORITES_LIST_ITEM_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { useShortLandscapeViewport } from '../../shared/hooks/useShortLandscapeViewport.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import { useDirectoryGeoPlaceLabels } from '../../shared/ui/useDirectoryGeoPlaceLabels.js';
import {
  directoryThumbFetchPriority,
  directoryThumbLoading,
} from '../../shared/ui/directoryThumbLoading.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { resolveDirectoryShopOpenAction } from './resolveDirectoryShopOpenAction.js';
import {
  shopsDirectoryQueryOptions,
  useShopsDirectoryRealtimeSync,
} from './hooks/useShopsDirectoryRealtimeSync.js';

const GEO_TIMEOUT_MS = 10_000;

interface UserPosition {
  readonly lat: number;
  readonly lng: number;
}

function acquireBrowserPosition(
  onSuccess: (position: UserPosition) => void,
  onError: (denied: boolean) => void,
): () => void {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.geolocation?.getCurrentPosition !== 'function'
  ) {
    onError(false);
    return () => undefined;
  }
  let cancelled = false;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      if (cancelled) {
        return;
      }
      onSuccess({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    },
    (error) => {
      if (cancelled) {
        return;
      }
      onError(error.code === error.PERMISSION_DENIED);
    },
    { timeout: GEO_TIMEOUT_MS, enableHighAccuracy: false },
  );
  return () => {
    cancelled = true;
  };
}

const SHOPS_DIRECTORY_SORT_OPTIONS_PLATFORM = [
  'nameAsc',
  'nameDesc',
  'orgAsc',
  'favoriteDesc',
  'distanceAsc',
] as const satisfies readonly DirectorySortId[];

const SHOPS_DIRECTORY_SORT_OPTIONS_TENANT = [
  'nameAsc',
  'nameDesc',
  'favoriteDesc',
  'distanceAsc',
] as const satisfies readonly DirectorySortId[];

export interface ShopsDirectoryScreenProps {
  /** When set, list only this tenant's public sales points (tenant browse CTA). */
  readonly filterTenantCode?: string | null;
}

/**
 * Platform `/shops` (Obchody tab root) — no content ←/Domů; shell owns chrome.
 * Tenant `/:tenantCode/shops` (guest browse from Organizace) — shell ← via hierarchicalBack → `/orgs`.
 */
export function ShopsDirectoryScreen(
  props: ShopsDirectoryScreenProps = {},
): JSX.Element {
  const { filterTenantCode = null } = props;
  const { t, i18n } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedIn = isAccountAccessUsable(session);
  const scopedTenant =
    typeof filterTenantCode === 'string' && filterTenantCode.trim().length > 0
      ? filterTenantCode.trim()
      : null;

  const persistTenant = resolveDirectoryShopsPersistTenant(scopedTenant);
  const [query, setQuery] = useState(() => readDirectoryShopsPersist(persistTenant)?.q ?? '');
  const [sort, setSort] = useState<DirectorySortId>(
    () => readDirectoryShopsPersist(persistTenant)?.sort ?? 'nameAsc',
  );
  const [orgCode, setOrgCode] = useState<string | null>(
    () =>
      scopedTenant !== null
        ? null
        : (readDirectoryShopsPersist(persistTenant)?.orgCode ?? null),
  );
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  /** True while getCurrentPosition is in flight for distanceAsc (restore/select). */
  const [geoPending, setGeoPending] = useState(
    () => (readDirectoryShopsPersist(persistTenant)?.sort ?? 'nameAsc') === 'distanceAsc',
  );
  const { viewMode, setViewMode } = useDirectoryViewModeSearch();
  const {
    density,
    setDensity,
    showToggle: showDensityToggle,
  } = useDirectoryDensity(DIRECTORY_DENSITY_STORAGE_KEYS.shops);
  const [selectedSalesPointId, setSelectedSalesPointId] = useState<number | null>(
    null,
  );
  const {
    savedViews,
    savedViewsLoading,
    savedViewsError,
    onRetrySavedViews,
    deletePending,
    renamePending,
    onRequestDeleteView,
    onRequestRenameView,
  } = useShopsDirectoryMapViews();
  const [persistTenantSeen, setPersistTenantSeen] = useState(persistTenant);
  if (persistTenantSeen !== persistTenant) {
    setPersistTenantSeen(persistTenant);
    const restored = readDirectoryShopsPersist(persistTenant);
    const restoredSort = restored?.sort ?? 'nameAsc';
    setQuery(restored?.q ?? '');
    setSort(restoredSort);
    setOrgCode(
      scopedTenant !== null ? null : (restored?.orgCode ?? null),
    );
    setUserPosition(null);
    // Keep distanceAsc in session until geo resolves — first persist must not demote.
    setGeoPending(restoredSort === 'distanceAsc');
  }

  useEffect(() => {
    writeDirectoryShopsPersist(
      persistTenant,
      {
        q: query,
        sort,
        orgCode: scopedTenant !== null ? null : orgCode,
      },
      {
        hasUserPosition: userPosition !== null,
        geoPending: sort === 'distanceAsc' && geoPending,
      },
    );
  }, [geoPending, orgCode, persistTenant, query, scopedTenant, sort, userPosition]);

  useEffect(() => {
    const onHydrated = (): void => {
      const restored = readDirectoryShopsPersist(persistTenant);
      const restoredSort = restored?.sort ?? 'nameAsc';
      setSort(restoredSort);
      setQuery(restored?.q ?? '');
      setOrgCode(
        scopedTenant !== null ? null : (restored?.orgCode ?? null),
      );
      setGeoPending(restoredSort === 'distanceAsc');
    };
    window.addEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    return () => {
      window.removeEventListener(DIRECTORY_UI_PREFS_HYDRATED_EVENT, onHydrated);
    };
  }, [persistTenant, scopedTenant]);

  // Nearest sort (or restored distanceAsc) — acquire position; keep it across other sorts.
  // Do NOT clear userPosition when leaving distanceAsc (sort switch preserves the fix).
  useEffect(() => {
    if (sort !== 'distanceAsc' || userPosition !== null) {
      return;
    }
    let sortResetHandle: number | undefined;
    const release = acquireBrowserPosition(
      (position) => {
        // Successful intentional acquire → durable session flag for later warm.
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
        setGeoPending(false);
      },
      (denied) => {
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        showToast({
          message: t(
            denied
              ? 'directory.filters.locationDenied'
              : 'directory.filters.locationUnavailable',
          ),
          variant: 'warn',
        });
        setUserPosition(null);
        setGeoPending(false);
        // Sync durable demotion before any timeout — unmount clearTimeout must not
        // leave DB/localStorage at distanceAsc (G3).
        writeDirectoryShopsPersist(
          persistTenant,
          {
            q: query,
            sort: 'nameAsc',
            orgCode: scopedTenant !== null ? null : orgCode,
          },
          { hasUserPosition: false, geoPending: false },
        );
        if (signedIn) {
          putDirectoryShopsSortRemote(apiBaseUrl, persistTenant, 'nameAsc');
        }
        // Defer setSort only — avoids react-hooks/set-state-in-effect sync cascade.
        sortResetHandle = window.setTimeout(() => {
          setSort('nameAsc');
        }, 0);
      },
    );
    return () => {
      release();
      if (sortResetHandle !== undefined) {
        window.clearTimeout(sortResetHandle);
      }
    };
  }, [
    apiBaseUrl,
    orgCode,
    persistTenant,
    query,
    scopedTenant,
    signedIn,
    sort,
    t,
    userPosition,
  ]);

  /**
   * Warm geolocation (G5) so distance/place meta appear on name/org/favorite
   * without forcing Distance (nearest) first on that load.
   *
   * G4 decision via decideDirectoryGeoWarm (session flag + Permissions probe):
   * - granted → always warm (silent getCurrentPosition; same options as distanceAsc).
   * - prompt + session flag → warm (SILENT best-effort; flag = prior success this tab).
   * - prompt + no flag → skip (never surprise-prompt on first visit).
   * - denied → clear session flag + skip.
   * - unavailable / queryFailed + flag → warm; without flag → skip.
   * Flag is written only after a successful fix; PERMISSION_DENIED on warm clears it.
   */
  useEffect(() => {
    if (userPosition !== null) {
      return;
    }
    if (typeof navigator === 'undefined') {
      return;
    }

    let cancelled = false;
    let releaseAcquire: (() => void) | undefined;

    const warmAcquire = (): void => {
      if (cancelled || releaseAcquire !== undefined) {
        return;
      }
      releaseAcquire = acquireBrowserPosition(
        (position) => {
          if (cancelled) {
            return;
          }
          writeDirectoryGeoAllowedFlag(true);
          setUserPosition(position);
          setGeoPending(false);
        },
        (denied) => {
          // Warm path is silent — never toast. Clear stale session flag on deny.
          if (denied) {
            writeDirectoryGeoAllowedFlag(false);
          }
        },
      );
    };

    const applyWarmDecision = (permission: DirectoryGeoPermissionProbe): void => {
      if (cancelled) {
        return;
      }
      const action = decideDirectoryGeoWarm(
        permission,
        readDirectoryGeoAllowedFlag(),
      );
      if (action === 'warm') {
        warmAcquire();
        return;
      }
      if (action === 'clearFlagAndSkip') {
        writeDirectoryGeoAllowedFlag(false);
      }
    };

    const permissionsQuery = navigator.permissions?.query;
    if (typeof permissionsQuery === 'function') {
      void permissionsQuery
        .call(navigator.permissions, { name: 'geolocation' })
        .then((status) => {
          if (
            status.state === 'granted' ||
            status.state === 'prompt' ||
            status.state === 'denied'
          ) {
            applyWarmDecision(status.state);
            return;
          }
          // Unexpected PermissionState — treat like unavailable (session-flag gate).
          applyWarmDecision('unavailable');
        })
        .catch(() => {
          applyWarmDecision('queryFailed');
        });
    } else {
      // No Permissions API (e.g. some Safari builds): session-flag best-effort warm.
      applyWarmDecision('unavailable');
    }

    return () => {
      cancelled = true;
      releaseAcquire?.();
    };
  }, [userPosition]);

  const apiSort = mapDirectorySortToApiSort(sort) ?? null;

  const directorySseConnected = useShopsDirectoryRealtimeSync();

  const shopsQuery = useInfiniteQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPoints(
      apiBaseUrl,
      scopedTenant,
      apiSort,
    ),
    queryFn: ({ pageParam, signal }) =>
      fetchPublicCustomerSalesPoints(apiBaseUrl, {
        cursor: pageParam,
        tenantCode: scopedTenant,
        sort: apiSort,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    ...shopsDirectoryQueryOptions(directorySseConnected),
  });

  const hasNextPage = shopsQuery.hasNextPage;
  const isFetchingNextPage = shopsQuery.isFetchingNextPage;
  const fetchNextPage = shopsQuery.fetchNextPage;

  // Distance sort needs the full list — drain remaining pages while active.
  // Popularity (favoriteDesc) must NOT full-drain — server pages already ranked.
  useEffect(() => {
    if (sort !== 'distanceAsc' || userPosition === null) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [sort, userPosition, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Map mode needs the full directory for markers — drain remaining pages while
  // active. Does NOT require userPosition (unlike distanceAsc). Switching to map
  // alone must never call acquireBrowserPosition.
  useEffect(() => {
    if (viewMode !== 'map') {
      return;
    }
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [viewMode, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSortChange = (next: DirectorySortId): void => {
    if (next === 'distanceAsc') {
      // Re-acquire only when we do not already hold a fix.
      if (userPosition === null) {
        setGeoPending(true);
      }
    } else {
      setGeoPending(false);
    }
    setSort(next);
    if (signedIn) {
      putDirectoryShopsSortRemote(apiBaseUrl, persistTenant, next);
    }
  };

  const items = useMemo(
    () => shopsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [shopsQuery.data?.pages],
  );

  const orgFacets = useMemo(
    () => (scopedTenant === null ? buildOrgFacets(items) : []),
    [items, scopedTenant],
  );

  const distanceSortActive = sort === 'distanceAsc' && userPosition !== null;
  /** Spec: group only after infinite pages are drained. */
  const distanceListReady =
    distanceSortActive && hasNextPage !== true && isFetchingNextPage !== true;
  /** Map markers: full drain complete and initial query not pending. */
  const mapReady =
    viewMode === 'map' &&
    !shopsQuery.isPending &&
    hasNextPage !== true &&
    isFetchingNextPage !== true;
  /** Show km + place whenever the user has allowed a live fix (any sort). */
  const showDistanceMeta = userPosition !== null;

  const visibleItems = useMemo(
    () =>
      filterDirectoryShops(items, {
        query,
        sort,
        orgCode: scopedTenant !== null ? null : orgCode,
        userLat: userPosition?.lat,
        userLng: userPosition?.lng,
      }),
    [items, orgCode, query, scopedTenant, sort, userPosition],
  );

  const [selectionFilterKey, setSelectionFilterKey] = useState(
    () => `${query}\0${orgCode ?? ''}\0${sort}`,
  );
  const nextSelectionFilterKey = `${query}\0${orgCode ?? ''}\0${sort}`;
  if (selectionFilterKey !== nextSelectionFilterKey) {
    setSelectionFilterKey(nextSelectionFilterKey);
    setSelectedSalesPointId(null);
  }

  const handleRequestMyLocation = (
    options?: ShopsDirectoryMapRequestMyLocationOptions,
  ): void => {
    acquireBrowserPosition(
      (position) => {
        writeDirectoryGeoAllowedFlag(true);
        setUserPosition(position);
      },
      (denied) => {
        options?.onDenied?.();
        if (denied) {
          writeDirectoryGeoAllowedFlag(false);
        }
        const aroundMeApply = options?.purpose === 'aroundMeApply';
        showToast({
          message: aroundMeApply
            ? `${t('directory.mapView.gpsDeniedTitle')}. ${t('directory.mapView.gpsDeniedBody')}`
            : t(
                denied
                  ? 'directory.filters.locationDenied'
                  : 'directory.filters.locationUnavailable',
              ),
          variant: 'warn',
        });
      },
    );
  };

  const distanceSections = useMemo(() => {
    if (!distanceListReady || userPosition === null) {
      return null;
    }
    return groupDirectoryShopsByDistance(visibleItems, userPosition.lat, userPosition.lng);
  }, [distanceListReady, userPosition, visibleItems]);

  const placeLabels = useDirectoryGeoPlaceLabels(items, showDistanceMeta, {
    acceptLanguage: i18n.language,
  });

  const filtersActive =
    query.trim().length > 0 ||
    (scopedTenant === null && orgCode !== null) ||
    sort !== 'nameAsc';

  const heading =
    scopedTenant !== null
      ? t('account.shop.salesPointsTitle')
      : t('retail.entry.shops.heading');
  const description =
    scopedTenant !== null
      ? t('tenantSelect.description')
      : t('retail.entry.shops.description');

  const bucketLabel = (bucket: DirectoryDistanceBucketId): string =>
    t(`directory.buckets.${bucket}`);

  const renderShopRow = (
    item: PublicCustomerSalesPointDTO,
    index: number,
  ): JSX.Element => {
    // Meta gated on live userPosition only — not on sort === distanceAsc (G3).
    const { distanceMeters, placeLabel } = buildShopDirectoryDistanceMeta({
      item,
      userLat: userPosition?.lat ?? null,
      userLng: userPosition?.lng ?? null,
      placeLabels: showDistanceMeta ? placeLabels : null,
    });
    return (
      <ShopDirectoryCard
        key={`${item.tenantCode}-${item.salesPointId}`}
        item={item}
        index={index}
        persistTenant={persistTenant}
        hideTenantName={scopedTenant !== null}
        distanceMeters={distanceMeters}
        placeLabel={placeLabel}
        listDensity={density}
      />
    );
  };

  // G5: map mode joins #main flex remaining-height chain (flex-1 min-h-0).
  // List mode stays content-sized with legacy bottom clearance under sticky cart.
  const mapLayoutActive =
    viewMode === 'map' && items.length > 0 && visibleItems.length > 0;
  const shortLandscape = useShortLandscapeViewport();
  // Phone landscape (844×390): hide page header + visible find toolbar so Leaflet
  // keeps leftover height. Keep an sr-only search input for hermetic G-FRAME churn.
  const compactMapChrome = mapLayoutActive && shortLandscape;

  return (
    <div
      className={[
        DIRECTORY_PAGE_STACK_CLASS,
        mapLayoutActive
          ? 'min-h-0 flex-1'
          : 'pb-[calc(5.5rem+env(safe-area-inset-bottom))]',
        compactMapChrome ? 'gap-2 pt-0' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="customer-shops-directory"
      data-directory-map-layout={mapLayoutActive ? 'active' : undefined}
      data-filter-tenant={scopedTenant ?? undefined}
    >
      {compactMapChrome ? null : (
        <DirectoryPageHeader title={heading} description={description} />
      )}

      {shopsQuery.isSuccess && items.length > 0 ? (
        <DirectoryListMapViewSwitch
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          listLabel={t('directory.mapView.list')}
          mapLabel={t('directory.mapView.map')}
          groupAriaLabel={t('directory.mapView.viewSwitchAria')}
          listAriaLabel={t('directory.mapView.listAria')}
          mapAriaLabel={t('directory.mapView.mapAria')}
          testIdPrefix="customer-shops-directory"
        />
      ) : null}

      {shopsQuery.isSuccess && items.length > 0 && compactMapChrome ? (
        // Off-viewport (not clipped sr-only): clipped sr-only sits under List/Map and
        // Playwright fill can click through to Seznam, leaving map mode.
        <label className="pointer-events-none fixed left-[-10000px] top-0 h-px w-px overflow-hidden">
          {t('directory.filters.searchShopsShort')}
          <input
            type="search"
            value={query}
            data-testid="customer-shops-directory-filters-search"
            className="pointer-events-auto"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </label>
      ) : null}

      {shopsQuery.isSuccess && items.length > 0 && !compactMapChrome ? (
        <div className="shrink-0">
          <DirectoryFindToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={handleSortChange}
            orgFacets={scopedTenant === null ? orgFacets : undefined}
            orgCode={orgCode}
            onOrgCodeChange={scopedTenant === null ? setOrgCode : undefined}
            sortOptions={
              scopedTenant === null
                ? SHOPS_DIRECTORY_SORT_OPTIONS_PLATFORM
                : SHOPS_DIRECTORY_SORT_OPTIONS_TENANT
            }
            searchPlaceholder={t('directory.filters.searchShopsShort')}
            testIdPrefix="customer-shops-directory-filters"
            densityMode={density}
            onDensityChange={setDensity}
            showDensityToggle={showDensityToggle ? viewMode === 'list' : false}
            densityTestId="customer-shops-directory-density"
            toolbarTestId="customer-shops-directory-find-toolbar"
          />
        </div>
      ) : null}

      {shopsQuery.isPending ? (
        <DirectoryResultsSkeleton
          density={density}
          size="lg"
          rowDensity="comfortable"
          listLayout="split2"
          cardsLayout="dense"
          testId="customer-shops-directory-skeleton"
        />
      ) : null}

      {shopsQuery.isError && items.length === 0 ? (
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('retail.entry.shops.loadError')}
          </p>
          <Button
            type="button"
            intent="secondary"
            block
            className="mt-4"
            data-testid="customer-shops-directory-retry"
            onClick={() => {
              void shopsQuery.refetch();
            }}
          >
            {t('retail.entry.shops.retry')}
          </Button>
        </Card>
      ) : null}

      {shopsQuery.isError && items.length > 0 ? (
        <Card padded className="mb-3">
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('retail.entry.shops.loadError')}
          </p>
          <Button
            type="button"
            intent="secondary"
            block
            className="mt-4"
            data-testid="customer-shops-directory-retry"
            onClick={() => {
              void shopsQuery.refetch();
            }}
          >
            {t('retail.entry.shops.retry')}
          </Button>
        </Card>
      ) : null}

      {!shopsQuery.isPending && !shopsQuery.isError && items.length === 0 ? (
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-shops-directory-empty"
          >
            {scopedTenant !== null
              ? t('account.shop.noSalesPoints')
              : t('retail.entry.shops.empty')}
          </p>
        </Card>
      ) : null}

      {items.length > 0 &&
      visibleItems.length === 0 &&
      !(distanceSortActive && !distanceListReady) ? (
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-shops-directory-no-matches"
          >
            {t('directory.filters.noMatches')}
          </p>
          {filtersActive ? (
            <Button
              type="button"
              intent="secondary"
              block
              className="mt-4"
              data-testid="customer-shops-directory-clear-filters"
              onClick={() => {
                setQuery('');
                setOrgCode(null);
                handleSortChange('nameAsc');
              }}
            >
              {t('directory.filters.clearAll')}
            </Button>
          ) : null}
        </Card>
      ) : null}

      {distanceSortActive && !distanceListReady ? (
        <p
          className="text-center text-sm text-[var(--color-on-surface-muted)]"
          data-testid="customer-shops-directory-distance-loading"
        >
          {t('retail.entry.shops.loading')}
        </p>
      ) : null}

      {/* G3: require visibleItems — filter wipe shows no-matches only; GPS-less shops still mount map. */}
      {viewMode === 'map' && items.length > 0 && visibleItems.length > 0 ? (
        <div
          className={`${CUSTOMER_CONTENT_FULL_CLASS} flex min-h-0 min-w-0 flex-1 flex-col`}
        >
          <ShopsDirectoryMap
            visibleItems={visibleItems}
            userPosition={userPosition}
            selectedSalesPointId={selectedSalesPointId}
            mapReady={mapReady}
            onSelectSalesPointId={setSelectedSalesPointId}
            onRequestMyLocation={handleRequestMyLocation}
            persistTenant={persistTenant}
            hideTenantName={scopedTenant !== null}
            onBackToList={() => {
              setViewMode('list');
            }}
            savedViews={savedViews}
            savedViewsLoading={savedViewsLoading}
            savedViewsError={savedViewsError}
            onRetrySavedViews={onRetrySavedViews}
            onRequestDeleteView={onRequestDeleteView}
            onRequestRenameView={onRequestRenameView}
            deletePending={deletePending}
            renamePending={renamePending}
          />
        </div>
      ) : null}

      {viewMode === 'list' &&
      visibleItems.length > 0 &&
      !(distanceSortActive && !distanceListReady) ? (
        <div className={`${CUSTOMER_CONTENT_FULL_CLASS} flex flex-col gap-3`}>
            {distanceSections !== null ? (
              distanceSections.map((section) => (
                <div
                  key={section.bucket}
                  className="flex flex-col gap-2"
                  data-testid={`customer-shops-directory-bucket-${section.bucket}`}
                >
                  <h2 className={`sticky ${CUSTOMER_TOP_BAR_CLEARANCE_TOP_CLASS} z-[9] bg-[var(--color-surface)] px-0.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]`}>
                    {bucketLabel(section.bucket)}
                  </h2>
                  <DirectoryResults
                    density={density}
                    listLayout="split2"
                    cardsLayout="dense"
                    testId={`customer-shops-directory-list-${section.bucket}`}
                  >
                    {section.items.map((item, index) => renderShopRow(item, index))}
                  </DirectoryResults>
                </div>
              ))
            ) : (
              <>
                <DirectoryResults
                  density={density}
                  listLayout="split2"
                  cardsLayout="dense"
                  testId="customer-shops-directory-list"
                >
                  {visibleItems.map((item, index) => renderShopRow(item, index))}
                </DirectoryResults>
                {hasNextPage && sort !== 'distanceAsc' ? (
                  <Button
                    type="button"
                    intent="secondary"
                    block
                    data-testid="customer-shops-directory-load-more"
                    disabled={isFetchingNextPage}
                    onClick={() => {
                      void fetchNextPage();
                    }}
                  >
                    {isFetchingNextPage
                      ? t('retail.entry.shops.loading')
                      : t('retail.entry.shops.loadMore')}
                  </Button>
                ) : null}
              </>
            )}
        </div>
      ) : null}
    </div>
  );
}

/** `/:tenantCode/shops` — that tenant's public sales-point picker (guest browse). */
export function TenantShopsDirectoryScreen(): JSX.Element {
  const tenantCode = useTenantCode();
  return <ShopsDirectoryScreen filterTenantCode={tenantCode} />;
}

function ShopDirectoryCard({
  item,
  index,
  persistTenant,
  hideTenantName = false,
  distanceMeters = null,
  placeLabel = null,
  listDensity = 'list',
}: {
  readonly item: PublicCustomerSalesPointDTO;
  readonly index: number;
  /** Session persist tenant segment for shops directory (platform or scoped). */
  readonly persistTenant: string;
  readonly hideTenantName?: boolean;
  readonly distanceMeters?: number | null;
  /** City / village / place from reverse geocode when user position is known. */
  readonly placeLabel?: string | null;
  /** Seznam vs Karty — Karty uses stack layout + card chrome `<li>`. */
  readonly listDensity?: DirectoryDensityMode;
}): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { effectiveTheme } = useTheme();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  // G5 Spec A: `/me` session memory only — BAN jwt LS / getToken sole proof.
  const signedIn = isGloballySignedInChrome({ session });
  const favoritesQuery = useSalesPointContactsByToken(signedIn);
  const [favoritePending, setFavoritePending] = useState(false);
  const [guestFavoriteEpoch, setGuestFavoriteEpoch] = useState(0);
  const openPending = false;
  /** Chip only on platform Obchody (not tenant-scoped). */
  const platformObchody = !hideTenantName;
  const cardsMode = listDensity === 'cards';
  const favoriteIds = useMemo(
    () => new Set((favoritesQuery.data?.items ?? []).map((fav) => fav.salesPointId)),
    [favoritesQuery.data?.items],
  );
  const isFavorite = signedIn
    ? favoriteIds.has(item.salesPointId)
    : (void guestFavoriteEpoch, isPendingFavoriteSalesPointId(item.salesPointId));
  const displayName = resolveLocalizedName(item.name, item.nameLocales, i18n.language);
  const displayDescription = resolveSalesPointDescription(
    item.description,
    item.descriptionLocales,
    i18n.language,
  );
  const tenantLabel =
    !hideTenantName && item.tenantName.length > 0 ? item.tenantName : null;
  const distanceLabel =
    typeof distanceMeters === 'number' && Number.isFinite(distanceMeters)
      ? formatDistanceMeters(distanceMeters, {
          meters: t('directory.distance.meters'),
          kilometers: t('directory.distance.kilometers'),
        })
      : null;

  const imageUrl = toSameOriginCatalogImageUrl(item.imageUrl, {
    tenantCode: item.tenantCode,
  });
  const imageUrlDark = toSameOriginCatalogImageUrl(item.imageUrlDark, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrl = toSameOriginCatalogImageUrl(item.tenantLogoUrl, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrlDark = toSameOriginCatalogImageUrl(item.tenantLogoUrlDark, {
    tenantCode: item.tenantCode,
  });

  // Do not invalidate the directory query on thumb failure — that refetches
  // `/public/customer-sales-points` (30/min public rate limit) and can flip the
  // list into a full-page error while cached rows still exist. Thumb falls back
  // to monogram via useStoreDirectoryThumb.

  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl,
    imageUrlDark,
    tenantLogoUrl,
    tenantLogoUrlDark,
    monogram: {
      id: item.salesPointId,
      displayName,
      code: item.code,
    },
    showTenantChip: platformObchody && (item.showTenantLogoOnStorePhoto ?? true),
    size: 'lg',
    logoChipRim: normalizeLogoChipRimSettings(item),
    logoChipBackground: normalizeLogoChipBackgroundSettings(item),
    logoChipRimTheme: effectiveTheme,
    loading: directoryThumbLoading(index),
    fetchPriority: directoryThumbFetchPriority(index),
    testId: `customer-shops-directory-thumb-${item.salesPointId}`,
  });

  const rowPressHref =
    resolveDirectoryShopOpenAction({
      customerShopUrl: item.customerShopUrl,
      targetTenantCode: item.tenantCode,
    })?.url;

  const handleFavorite = async (): Promise<void> => {
    if (isFavorite) {
      // Signed-in unfavorite is Oblíbené-hub only (/favorites) — Obchody star is add-only.
      return;
    }
    // Guest path (D-GuestExclusion): stage only — no directory cache invalidate,
    // no optimistic favoriteCount mutation.
    if (!signedIn) {
      stagePendingFavoriteSpContact({
        tenantCode: item.tenantCode,
        salesPointId: item.salesPointId,
        salesPointSlug: item.code,
        code: item.code,
        name: item.name,
        nameLocales: item.nameLocales ?? null,
        location: item.location,
        customerPresence: item.customerPresence,
        tenantId: item.tenantId,
        tenantName: item.tenantName,
        customerShopUrl: item.customerShopUrl,
        imageUrl: item.imageUrl,
        imageUrlDark: item.imageUrlDark,
        tenantLogoUrl: item.tenantLogoUrl,
        tenantLogoUrlDark: item.tenantLogoUrlDark,
        showLogoChipRimLight: item.showLogoChipRimLight,
        showLogoChipRimDark: item.showLogoChipRimDark,
        logoChipRimColorLight: item.logoChipRimColorLight,
        logoChipRimColorDark: item.logoChipRimColorDark,
        showLogoChipBackgroundLight: item.showLogoChipBackgroundLight,
        showLogoChipBackgroundDark: item.showLogoChipBackgroundDark,
        logoChipBackgroundColorLight: item.logoChipBackgroundColorLight,
        logoChipBackgroundColorDark: item.logoChipBackgroundColorDark,
      });
      setGuestFavoriteEpoch((n) => n + 1);
      return;
    }
    setFavoritePending(true);
    try {
      await setSalesPointFavorite({
        apiBaseUrl,
        body: { salesPointId: item.salesPointId, favorite: true },
      });
      const customerKey = session?.customerId ?? 'bearer';
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.salesPointContacts(customerKey),
      });
      if (readDirectoryShopsPersist(persistTenant)?.sort === 'favoriteDesc') {
        invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
      }
    } catch {
      showToast({
        message: t('errors.spContact.recordTransport'),
        variant: 'danger',
      });
    } finally {
      setFavoritePending(false);
    }
  };

  const handleUnfavoriteGuest = (): void => {
    removePendingFavoriteSpContact(item.salesPointId);
    setGuestFavoriteEpoch((n) => n + 1);
  };

  const row = (
    <ShopDirectoryRowView
      thumb={thumbProps}
      title={displayName}
      tenantLabel={tenantLabel}
      location={resolveShopLocationLabelWithT(
        {
          customerPresence: item.customerPresence,
          location: item.location,
        },
        t,
      )}
      description={displayDescription}
      placeLabel={placeLabel}
      distanceLabel={distanceLabel}
      rowPressHref={rowPressHref}
      rowPressDisabled={openPending || rowPressHref === undefined}
      rowPressTestId={`customer-shops-directory-open-${item.salesPointId}`}
      showChevron
      density="comfortable"
      layout={cardsMode ? 'stack' : 'inline'}
      testId={`customer-shops-directory-row-${item.salesPointId}`}
      trailing={
        <button
          type="button"
          className={[
            'inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent',
            'hover:bg-[var(--color-surface-muted)]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            'disabled:cursor-not-allowed disabled:opacity-[0.55]',
            isFavorite
              ? 'text-[var(--color-warning)]'
              : 'text-[var(--color-on-surface-muted)]',
          ].join(' ')}
          data-testid={`customer-shops-directory-star-${item.salesPointId}`}
          title={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
          aria-label={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
          aria-pressed={isFavorite}
          disabled={favoritePending || openPending}
          onClick={(event) => {
            event.stopPropagation();
            if (isFavorite && !signedIn) {
              handleUnfavoriteGuest();
              return;
            }
            void handleFavorite();
          }}
        >
          <FavoriteStarIcon filled={isFavorite} />
        </button>
      }
    />
  );

  if (cardsMode) {
    return (
      <li
        className={[DIRECTORY_CARD_ITEM_CLASS, DIRECTORY_CARD_PAD_CLASS].join(' ')}
        data-testid={`customer-shops-directory-card-${item.salesPointId}`}
        data-density="cards"
      >
        {row}
      </li>
    );
  }

  return <li className={DIRECTORY_FAVORITES_LIST_ITEM_CLASS}>{row}</li>;
}
